import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { LocalStorageService } from '@isaia/local-storage';
import { defineSentryUser } from '../sentry';
import { getUserFullName } from './auth-full-name.pipe';
import { AuthUser } from './auth-user.model';

export interface AuthState {
  config?: {
    storeId?: string | null;
  };
  // Dev/test password-login token (v2 only); Entra sessions live in MSAL's own cache.
  devToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthRepository {
  private readonly localStorageService = inject(LocalStorageService);
  // Persisted, user-selected config (e.g. active store) — survives reloads.
  private readonly $store = signal<AuthState | undefined>(undefined);
  // Current user profile, loaded from the API (/me) after Entra sign-in. Not
  // persisted: it is re-fetched on each app start while an MSAL session exists.
  private readonly $userState = signal<AuthUser | undefined>(undefined);
  // Last authentication failure surfaced to the login screen (e.g. the API
  // rejected the Entra token during init). Not persisted.
  private readonly $authErrorState = signal<string | undefined>(undefined);

  // Resolves once AuthService.initAuth has finished (profile loaded or not).
  // Route guards await this so they never evaluate $isLoggedIn before the
  // initial profile fetch completes — otherwise the initial navigation races
  // initAuth and a logged-in user is wrongly sent to the login screen.
  private resolveReady!: () => void;
  private readonly ready = new Promise<void>((resolve) => (this.resolveReady = resolve));

  private readonly $config = computed(() => this.$store()?.config);
  public readonly $authError = computed(() => this.$authErrorState());
  public readonly $devToken = computed(() => this.$store()?.devToken);
  public readonly $user = computed(() => this.$userState());
  public readonly $email = computed(() => this.$user()?.email || '');
  public readonly sentToProductionEmails = computed(() => this.$user()?.sentToProductionEmails || []);
  public readonly $isLoggedIn = computed(() => !!this.$user()?.email);
  public readonly $storeId = computed(() => this.$config()?.storeId || this.$user()?.defaultStore);
  public readonly fullName = computed(() => getUserFullName(this.$user()));
  public readonly isAdmin = computed(() => !!this.$user()?.permissions?.length);
  public readonly isMtmUser = computed(() => !!this.$user()?.defaultStore);
  public readonly isContextWholesale = computed(() => this.$user()?.defaultStore?.length === 6);

  constructor() {
    this.localStorageService.syncValue(
      'auth',
      () => this.$store(),
      (state) => this.setStore(state),
    );

    effect(() => {
      defineSentryUser(this.$user());
    });
  }

  public setStore(state?: AuthState) {
    this.$store.set(state);
  }

  public setUser(user?: AuthUser) {
    this.$userState.set(user);
  }

  public setAuthError(message?: string) {
    this.$authErrorState.set(message);
  }

  /** Await auth initialization (used by route guards). */
  public whenReady() {
    return this.ready;
  }

  /** Called by AuthService.initAuth once startup auth has settled. */
  public markReady() {
    this.resolveReady();
  }

  public setDevToken(devToken?: string) {
    this.$store.update((state) => ({ ...state, devToken }));
  }

  public updateConfig(config: AuthState['config']) {
    this.$store.update((state) => {
      return { ...state, config: { ...state?.config, ...config } };
    });
  }

  public onLogout(fn: () => void) {
    return effect(() => {
      if (!this.$isLoggedIn()) {
        untracked(fn);
      }
    });
  }
}
